import React, {useEffect, useReducer} from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import Select from "react-select";
import moment from "moment-timezone";

import ai from "../../helpers/axios";
import TextAreaInput from "../textarea-input";
import shortenName from "../../helpers/shorten-name";
import { LoadingButton } from "../shared/loading-button";

const CATEGORY_OPTIONS = [
  { label: "Accounting", value: "Accounting" },
  { label: "Flight Ops", value: "Flight Ops" },
  { label: "Catering/Ground", value: "Catering/Ground" },
  { label: "Other", value: "Other" },
];

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
      };
    }

    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }

    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }

    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: null,
        note: "",
        category: null,
      };
    }

    default:
      break;
  }
  return state;
};

const TripNotesModule = (props) => {
  const { getAccessTokenSilently } = useAuth0();

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: null,
    note: "",
    category: null,
    notes: [...props.notes],
    loading: false,
  };

  const isTripNotes = props.noteType === "tripNotes";

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    props.setUnsavedChanges?.({
      section: 'notesModal',
      value: !!state.showModal
    });
  }, [props.setUnsavedChanges, state.showModal]);

  const handleAddNote = (e) => {
    e.preventDefault();

    dispatch({
      type: "openModal",
    });
  };

  const handleEditNote = (e, index) => {
    e.preventDefault();

    const note = state.notes[index];
    if (!note) {
      return;
    }

    dispatch({
      type: "openModal",
      payload: note,
    });
  };

  const handleDeleteNote = (e, index) => {
    e.preventDefault();

    const note = state.notes[index];
    if (!note) {
      return;
    }

    dispatch({
      type: "openDeleteModal",
      payload: note,
    });
  };

  const handleCloseModal = () => {
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();

    if (!state.note || (isTripNotes && !state.category)) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    let response;
    const token = await getAccessTokenSilently();
    if (state._id) {
      response = await ai.auth(token).put(
        `/api/trips/${props.tripId}/updateNote/${state._id}`,
        {
          noteText: state.note,
          noteType: props.noteType,
          category: state.category,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ).catch((error) => {
        Sentry.captureException(error);
      });
    } else {
      response = await ai.auth(token).post(
        `/api/trips/${props.tripId}/addNote`,
        {
          noteText: state.note,
          noteType: props.noteType,
          category: state.category,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ).catch((error) => {
        Sentry.captureException(error);
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      handleCloseModal();

      props.parentDispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }
  };

  const handleDeleteNoteConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const token = await getAccessTokenSilently();
    const response = await ai.auth(token).delete(`/api/trips/${props.tripId}/deleteNote/${state._id}`, {
      data: {
        noteType: props.noteType,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).catch((error) => {
      Sentry.captureException(error);
    });

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      handleCloseModal();

      props.parentDispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }
  };

  return (
    <React.Fragment>
      <div
        className="trip-notes-module"
        id={`trip-${props.noteType}`}
      >
        <h5 className="mb-1 d-flex">
          {props.title} Notes

          {!props.readOnly && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAddNote}
              title="Add"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <div className="trip-notes-table pb-0">
          <table>
            <thead>
            <tr>
              <th>Created By</th>
              {isTripNotes && <th>Entered At</th>}
              {isTripNotes && <th>Category</th>}
              <th>Note</th>
              <th className="text-align-right">Actions</th>
            </tr>
            </thead>
            <tbody>
            {!state.notes?.length && <tr>
              <td colSpan={isTripNotes ? 4 : 2}>No {props.title?.toLowerCase()} notes are saved yet</td>
              <td/>
            </tr>}
            {state.notes?.map((note, index) => {
              return (
                <tr key={note._id}>
                  <td>{shortenName(note.createdBy?.name)}</td>
                  {isTripNotes && (
                    <td>
                      {note.createdAt
                        ? moment(note.createdAt).format("M/D/YY - h:mma")
                        : "N/A"}
                    </td>
                  )}
                  {isTripNotes && <td>{note.category || "N/A"}</td>}
                  <td>{note.note}</td>
                  <td>
                    {!props.readOnly && (
                      <div className="d-flex justify-content-end">
                        <button
                          className="base-button icon-button icon-button-small evo-blue"
                          onClick={(e) => {
                            handleEditNote(e, index);
                          }}
                          title="Edit"
                        >
                          <span className="material-icons">edit</span>
                        </button>

                        <button
                          className="base-button icon-button icon-button-small btn-danger ml-2"
                          onClick={(e) => handleDeleteNote(e, index)}
                          title="Delete"
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state._id ? "Edit" : "Add"} {props.title} Note</h4>
        </div>

        <div className="modal-body edit-note-modal">
          <form onSubmit={handleSubmitNote} className="w-100 base-form">
            {isTripNotes && (
              <div className="form-group">
                <label>
                  Category <span className="required">*</span>
                </label>
                <Select
                  classNamePrefix="select"
                  options={CATEGORY_OPTIONS}
                  placeholder="Select a Category"
                  value={
                    CATEGORY_OPTIONS.find((o) => o.value === state.category) || null
                  }
                  onChange={(selected) =>
                    dispatch({
                      type: "fieldChanged",
                      field: "category",
                      value: selected?.value || null,
                    })
                  }
                  name="category"
                  menuPosition="fixed"
                />
              </div>
            )}

            <TextAreaInput
              type="text"
              name="note"
              value={state.note}
              label="Note"
              placeholder="Add note"
              handleChange={handleInputChange}
              required
            />

            <div className="w-100 mt-20 d-flex gap-20">
              <LoadingButton className="base-button evo-blue flex-half" type="submit" isLoading={state.saving}>
                Submit
              </LoadingButton>

              <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                      disabled={state.saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete {props.title} Note</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This {props.title?.toLowerCase()} note will be permanently deleted from the trip. Are you sure you want to
            continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <LoadingButton className="base-button btn-danger flex-half" type="button" onClick={handleDeleteNoteConfirm}
                    isLoading={state.saving}>
              Delete Note
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </React.Fragment>
  );
};

export default TripNotesModule;
